const RECOVERY_STORAGE_KEY = "evolys:deployment-recovery-at";
const RECOVERY_THROTTLE_MS = 60_000;

export function shouldAttemptDeploymentRecovery(
  lastAttempt: number | null,
  now: number,
) {
  if (lastAttempt === null) return true;

  const elapsed = now - lastAttempt;
  return elapsed < 0 || elapsed >= RECOVERY_THROTTLE_MS;
}

function readLastRecoveryAttempt(): number | null {
  try {
    const stored = window.sessionStorage.getItem(RECOVERY_STORAGE_KEY);
    if (!stored) return null;

    const timestamp = Number(stored);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

function rememberRecoveryAttempt(timestamp: number) {
  try {
    window.sessionStorage.setItem(RECOVERY_STORAGE_KEY, String(timestamp));
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // browser reload still provides the best recovery path in that case.
  }
}

/**
 * Recovers an already-open Vite application after an immutable chunk is
 * replaced by a rolling deployment. Vite emits `vite:preloadError` before
 * surfacing the dynamic-import exception.
 */
export function installDeploymentRecovery() {
  if (typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    const now = Date.now();
    const lastAttempt = readLastRecoveryAttempt();

    // Avoid an infinite refresh loop when the cause is a persistent network
    // failure or browser extension rather than deployment version skew.
    if (!shouldAttemptDeploymentRecovery(lastAttempt, now)) {
      return;
    }

    event.preventDefault();
    rememberRecoveryAttempt(now);
    window.location.reload();
  });
}
